import React, { ReactNode, useEffect, useMemo } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import useEffectiveBrand from '../hooks/useEffectiveBrand';
import buildBrandTheme from './brandTheme';
import { useSkin } from './SkinProvider';
import { loadBrandFont } from './lazyFonts';

/**
 * ChakraProvider whose theme is built at runtime from the EFFECTIVE brand —
 * the company's brand package (item 35) after the per-device brand source is
 * applied (item 76: own / Basix colours + my logo / stock Basix). While the
 * brand is loading, the stock Basix theme renders.
 *
 * The SKIN (item 61) also feeds the theme: 'accountant' short-circuits to the
 * monochrome typewriter-ledger theme, so toggling it on Tools → Appearance
 * repaints the whole app live (the memo re-runs on skin change).
 */
const BrandThemeProvider = ({ children }: { children: ReactNode }) => {
  const { brand } = useEffectiveBrand();
  const { skin, ledgerTone, colorPref } = useSkin();
  const fontFamily = brand?.fontFamily;
  // item 1516 — Montserrat/Raleway/Lato/Inter no longer ship in the main CSS; the moment the
  // tenant's face resolves to one of them, pull its chunk so the fallback sans never lingers.
  useEffect(() => { loadBrandFont(fontFamily); }, [fontFamily]);
  const theme = useMemo(
    () => buildBrandTheme(brand, skin, ledgerTone, colorPref),
    [brand, skin, ledgerTone, colorPref],
  );

  return <ChakraProvider theme={theme}>{children}</ChakraProvider>;
};

export default BrandThemeProvider;
